import { useMemo } from 'react';
import MixpanelService from '../../services/MixpanelService';
import isOmpPlatform from '../utils/isOmpPlatform';
import TradeDetails from '../models/TradeDetails';
import { BidStatusName } from '../types/auction';
import segmentConfig from '../constants/segmentConfig';

const getNetworkType = ({ displayName, isInternalTrade }) => {
  if (displayName) {
    return displayName === segmentConfig.networkType.open
      ? segmentConfig.networkType.open
      : segmentConfig.networkType.internal;
  }

  return isInternalTrade
    ? segmentConfig.networkType.internal
    : segmentConfig.networkType.open;
};

export const getVinBasicProperties = (tradeDetails: any) => {
  return {
    is_as_is: !!(tradeDetails?.isAsIs || tradeDetails?.asIs),
    is_floor_price: !!tradeDetails?.tradeFloorPrice,
    is_in_stock: !!(tradeDetails?.isLanded || tradeDetails?.inStock),
    make_value:
      tradeDetails?.vehicleSummary?.model?.manufacturerName ||
      tradeDetails?.make?.displayName ||
      tradeDetails?.make?.name,
    model_value:
      tradeDetails?.vehicleSummary?.model?.displayName ||
      tradeDetails?.model?.displayName ||
      tradeDetails?.model?.name,
    network_type: getNetworkType({
      displayName: tradeDetails?.network?.displayName,
      isInternalTrade: tradeDetails?.isInternalTrade
    }),
    launch_id:
      tradeDetails?.auctionSummary?.id?.toString() ||
      tradeDetails?.auctionId?.toString() ||
      null,
    trim_value:
      tradeDetails?.vehicleSummary?.trim || tradeDetails?.trim || null,
    vehicle_id: tradeDetails?.id?.toString() || null,
    vin_value: tradeDetails?.vin,
    year_value: tradeDetails?.vehicleSummary?.modelYear || tradeDetails?.year
  };
};

export const getSellerProperties = (tradeSummary: any) => {
  return {
    seller_org_ecs_id: tradeSummary?.dealer?.customerId?.toString() || null,
    seller_org_name_value:
      tradeSummary?.dealer?.displayName ||
      tradeSummary?.dealerInfo?.displayName,
    seller_org_source_system_id:
      tradeSummary?.dealer?.id?.toString() ||
      tradeSummary?.dealerInfo?.id?.toString() ||
      null,
    seller_rep_source_system_id:
      tradeSummary?.dealer?.appraisedBy?.id?.toString() ||
      tradeSummary?.appraisedBy?.id?.toString() ||
      null,
    seller_source_system_type: segmentConfig.sourceSystemType.traderev
  };
};

export const getVinPostSaleProperties = (tradeDetails: TradeDetails) => {
  return {
    sale_date: tradeDetails?.statusDates?.acquired,
    sale_price: tradeDetails?.billOfSale?.salePrice
  };
};

export const getVinLaunchProperties = (tradeDetails: TradeDetails) => {
  return {
    launch_count: tradeDetails?.auctionLaunchCount || 0,
    most_recent_launch_date: tradeDetails?.statusDates?.launched || undefined,
    vehicle_created_date: tradeDetails?.decodedAt
  };
};

export const getVinSaleEventProperties = (tradeDetails: TradeDetails) => {
  return {
    sale_event_id:
      tradeDetails?.saleEventSummary?.id || tradeDetails?.saleEventId,
    sale_event_name: tradeDetails?.saleEventSummary?.name,
    sale_event_vehicle_count:
      tradeDetails?.saleEventSummary?.numberOfTrades || null
  };
};

export const getGenericBiddingListProperties = (tradeDetails: TradeDetails) => {
  return {
    generic_bid_status:
      BidStatusName[tradeDetails?.auctionSummary?.userBidStatus],
    generic_close_to_floor: tradeDetails?.tradeFloorPrice?.isCloseToFloorPrice,
    generic_top_bid: tradeDetails?.auctionSummary?.maxBid,
    generic_total_bids: tradeDetails?.auctionSummary?.numBids
  };
};

const useAnalytics = () => {
  const track = useMemo(() => {
    if (isOmpPlatform()) {
      if (window?.kar?.analytics?.trackWeb5) {
        return window.kar.analytics.trackWeb5;
      }

      return () => {};
    }

    // Use mixpanel service
    const mixpanel = MixpanelService.get();
    return mixpanel.track.bind(mixpanel);
  }, []);

  return track;
};

export default useAnalytics;
